import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { ensure, isNil } from "lopermut"
import { useHasCompanyFeature } from "modules/auth/hooks/useHasCompanyFeature"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useCapabilitiesDictionary } from "modules/capabilities/hooks/useCapabilitiesDictionary"
import { useCapabilitiesQuery } from "modules/capabilities/hooks/useCapabilitiesQuery"
import { useMarketingChannelOptions } from "modules/customers/hooks/useMarketingChannelOptions"
import { useTagsQuery } from "modules/customers/hooks/useTagsQuery"
import { useCanonicalTopicsQuery } from "modules/insights/hooks/useCanonicalTopicsQuery"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useEmployeesQuery } from "modules/settings/hooks/useEmployeesQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route as ConversationsListRoute } from "routes/_base/_auth/conversations/list/route"
import {
  CapabilityExecutionStatus,
  CapabilityKind,
  ChannelEnum,
  ClusterDomainEnum,
  CompanyUsageKind,
  ConversationStatusEnum,
  FeedbackClusterDomainEnum,
  FlowEnum,
  SENTIMENTS,
  SupportClusterDomainEnum,
} from "schema"

const LiveOrClosedEnum = {
  Live: "Live",
  Closed: "Closed",
} as const

const getDomainsEnum = (hasFeedback: boolean) => {
  let clusters: Record<string, string> = SupportClusterDomainEnum

  if (hasFeedback) {
    clusters = {
      ...FeedbackClusterDomainEnum,
      ...clusters,
    }
  }

  return clusters
}

export function ConversationsFilter() {
  const { t } = useTranslation()
  const search = ConversationsListRoute.useSearch()
  const navigate = ConversationsListRoute.useNavigate()
  const hasFeedback = useHasCompanyFeature(CompanyUsageKind.Feedback)
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const labels = useFilterBarLabels()

  const { data: campaigns = [] } = useCampaignsQuery({
    query: {
      filterBy: { archived: false },
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const { data: goals = [] } = useGoalsQuery({
    query: {
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const { data: capabilities = [] } = useCapabilitiesQuery()

  const { data: canonicalTopics = [] } = useCanonicalTopicsQuery({
    query: {
      domain: search.filterBy?.domain ?? ClusterDomainEnum.VoiceOfCustomer,
      withSnapshots: true,
    },
    enabled: !!search.filterBy?.domain,
  })

  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { data: employees = [] } = useEmployeesQuery()
  const { data: tags = [] } = useTagsQuery()
  const { data: integrationsStatus } = useIntegrationsStatusQuery()
  const marketingChannelOptions = useMarketingChannelOptions()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  function onChangeFilter<T extends keyof NonNullable<typeof search.filterBy>>(
    key: T,
    value: unknown,
  ) {
    const newFilterBy = { ...search.filterBy }

    if (isNil(value)) {
      delete newFilterBy[key]
    } else {
      newFilterBy[key] = value as NonNullable<typeof search.filterBy>[T]
    }

    if (key === "domain") {
      delete newFilterBy.canonicalId
    }

    if (key === "capabilityId" && isNil(value)) {
      delete newFilterBy.capabilityExecutionStatus
    }

    navigate({
      search: {
        ...search,
        page: 1,
        filterBy: newFilterBy,
      },
    })
  }

  function onClearFilters() {
    navigate({
      search: {
        ...search,
        filterBy: undefined,
        query: undefined,
        page: 1,
      },
    })
  }

  const subscribedToFields: FilterBarField[] = integrationsStatus?.shopify
    ? [
        {
          key: "subscribedTo",
          type: "multiSelect",
          label: t("customers.filters.subscribedTo"),
          value: search.filterBy?.subscribedTo,
          onChange: (value) => onChangeFilter("subscribedTo", value),
          options: marketingChannelOptions,
        },
      ]
    : []

  const fields: FilterBarField[] = [
    {
      key: "campaignId",
      type: "multiSelect",
      label: t("conversations.filters.campaign"),
      value: search.filterBy?.campaignId,
      onChange: (value) => onChangeFilter("campaignId", value),
      options: campaigns.map((campaign) => {
        const hasName = !!campaign.name && campaign.name.length > 0

        return {
          value: campaign.id,
          label: hasName ? ensure(campaign.name) : t("campaigns.untitled"),
          className: hasName ? undefined : "text-muted-foreground",
        }
      }),
    },
    {
      key: "status",
      type: "multiSelect",
      label: t("conversations.filters.status"),
      value: search.filterBy?.status?.flatMap((status) =>
        status === "EngagedOrAchieved"
          ? [ConversationStatusEnum.Engaged, ConversationStatusEnum.Achieved]
          : status,
      ),
      onChange: (value) => onChangeFilter("status", value),
      options: Object.values(ConversationStatusEnum).map((status) => ({
        value: status,
        label: t(`conversations.statuses.${status}`),
      })),
    },
    {
      key: "channel",
      type: "multiSelect",
      label: t("conversations.filters.channel"),
      value: search.filterBy?.channel,
      onChange: (value) => onChangeFilter("channel", value),
      // Email is a legacy channel superseded by Gmail and Outlook
      options: Object.values(ChannelEnum)
        .filter((channel) => channel !== ChannelEnum.Email)
        .map((channel) => ({
          value: channel,
          label: t(`campaigns.channel.${channel.toLowerCase()}.label`),
        })),
    },
    {
      key: "segmentId",
      type: "multiSelect",
      label: t("conversations.filters.segment"),
      value: search.filterBy?.segmentId,
      onChange: (value) => onChangeFilter("segmentId", value),
      options: segmentGroups.flatMap((segmentGroup) =>
        segmentGroup.segments.map((segment) => ({
          value: segment.id,
          label: getSegmentTranslation(segment.name, segmentGroup),
          group: getSegmentGroupTranslation(segmentGroup) || segmentGroup.name,
        })),
      ),
    },
    {
      key: "tagIds",
      type: "multiSelect",
      label: t("customers.tags.filterLabel"),
      value: search.filterBy?.tagIds,
      onChange: (value) => onChangeFilter("tagIds", value),
      options: tags.map((tag) => ({ value: tag.id, label: tag.name })),
    },
    ...subscribedToFields,
    {
      key: "dateRange",
      type: "dateRange",
      label: t("shared.dateRange.placeholder"),
      value: search.filterBy?.dateRange,
      onChange: (value) => onChangeFilter("dateRange", value),
    },
    {
      key: "isLive",
      type: "select",
      label: t("conversations.filters.activity"),
      value: mapIsLiveToLabel(search.filterBy?.isLive),
      onChange: (value) => onChangeFilter("isLive", isNil(value) ? value : value === "Live"),
      options: Object.values(LiveOrClosedEnum).map((value) => ({
        value,
        label: t(`conversations.filters.${value.toLowerCase()}`),
      })),
    },
    {
      key: "flow",
      type: "multiSelect",
      label: t("conversations.filters.flow"),
      value: search.filterBy?.flow,
      onChange: (value) => onChangeFilter("flow", value),
      options: Object.values(FlowEnum).map((flow) => ({
        value: flow,
        label: t(`campaigns.configuration.setup.flow.${flow.toLowerCase()}.title`),
      })),
    },
    {
      key: "goalId",
      type: "multiSelect",
      label: t("conversations.filters.goal"),
      value: search.filterBy?.goalId,
      onChange: (value) => onChangeFilter("goalId", value),
      options: goals.map((goal) => ({ value: goal.id, label: goal.displayName })),
    },
    {
      key: "sentiment",
      type: "multiSelect",
      label: t("conversations.filters.sentiment"),
      value: search.filterBy?.sentiment,
      onChange: (value) => onChangeFilter("sentiment", value),
      options: SENTIMENTS.filter(({ label }) => label.length > 0).map((sentiment) => ({
        value: sentiment.value,
        label: t(sentiment.label),
      })),
    },
    {
      key: "hasSupport",
      type: "select",
      label: t("conversations.filters.hasSupport"),
      value: mapHasSupportToLabel(search.filterBy?.hasSupport),
      onChange: (value) => onChangeFilter("hasSupport", mapLabelToHasSupport(value)),
      options: [
        { value: "questions", label: t("conversations.filters.hasSupportQuestions") },
        { value: "tickets", label: t("conversations.filters.hasSupportTickets") },
        { value: "noTickets", label: t("conversations.filters.hasSupportNoTickets") },
        { value: "any", label: t("conversations.filters.hasSupportAny") },
      ],
    },
    {
      key: "hasBusinessOpportunities",
      type: "select",
      label: t("conversations.filters.hasBusinessOpportunities"),
      value: mapBooleanFilterToLabel(search.filterBy?.hasBusinessOpportunities),
      onChange: (value) =>
        onChangeFilter("hasBusinessOpportunities", isNil(value) ? value : value === "true"),
      options: [
        { value: "true", label: t("conversations.filters.hasBusinessOpportunities") },
        { value: "false", label: t("conversations.filters.noBusinessOpportunities") },
      ],
    },
    {
      key: "hasInternalNotes",
      type: "select",
      label: t("conversations.filters.hasInternalNotes"),
      value: mapBooleanFilterToLabel(search.filterBy?.hasInternalNotes),
      onChange: (value) =>
        onChangeFilter("hasInternalNotes", isNil(value) ? value : value === "true"),
      options: [
        { value: "true", label: t("conversations.filters.hasInternalNotes") },
        { value: "false", label: t("conversations.filters.noInternalNotes") },
      ],
    },
    {
      key: "noteMentionEmployeeId",
      type: "multiSelect",
      label: t("conversations.filters.noteMentionEmployee"),
      value: search.filterBy?.noteMentionEmployeeId,
      onChange: (value) => onChangeFilter("noteMentionEmployeeId", value),
      options: employees
        .filter((employee) => !employee.archivedAt)
        .map((employee) => ({ value: employee.id, label: employee.name ?? employee.email }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    {
      key: "hasAttachments",
      type: "select",
      label: t("conversations.filters.hasAttachments"),
      value: mapHasAttachmentsToLabel(search.filterBy?.hasAttachments),
      onChange: (value) => onChangeFilter("hasAttachments", mapLabelToHasAttachments(value)),
      options: [
        { value: "user", label: t("conversations.filters.hasAttachmentsUser") },
        { value: "assistant", label: t("conversations.filters.hasAttachmentsAssistant") },
        { value: "both", label: t("conversations.filters.hasAttachmentsBoth") },
      ],
    },
    {
      key: "capabilityId",
      type: "multiSelect",
      label: t("conversations.filters.capability"),
      value: search.filterBy?.capabilityId,
      onChange: (value) => onChangeFilter("capabilityId", value),
      options: capabilities
        .map((capability) => {
          const dictionaryName = capabilitiesDictionary[capability.identifier]?.name
          const customCapabilityName =
            capability.kind === CapabilityKind.Webhook ||
            capability.kind === CapabilityKind.Instruction ||
            capability.kind === CapabilityKind.MCP
              ? capability.name
              : undefined

          return {
            value: capability.id,
            label:
              customCapabilityName || dictionaryName || capability.name || capability.identifier,
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    {
      key: "capabilityExecutionStatus",
      type: "select",
      label: t("conversations.filters.capabilityExecutionStatus"),
      condition: !!search.filterBy?.capabilityId,
      value: search.filterBy?.capabilityExecutionStatus,
      onChange: (value) => onChangeFilter("capabilityExecutionStatus", value),
      options: [
        CapabilityExecutionStatus.Success,
        CapabilityExecutionStatus.Unsuccess,
        CapabilityExecutionStatus.Error,
        CapabilityExecutionStatus.Skipped,
      ].map((status) => ({
        value: status,
        label: t(`conversations.filters.capabilityExecutionStatuses.${status}`),
      })),
    },
    {
      key: "domain",
      type: "select",
      label: t("conversations.filters.domain"),
      value: search.filterBy?.domain,
      onChange: (value) => onChangeFilter("domain", value),
      options: Object.values(getDomainsEnum(hasFeedback)).map((domain) => ({
        value: domain,
        label: t(`dashboard.domains.${domain}`),
      })),
    },
    {
      key: "canonicalId",
      type: "select",
      label: t("conversations.filters.canonicalTopic"),
      condition: !!search.filterBy?.domain && canonicalTopics.length > 0,
      value: search.filterBy?.canonicalId,
      onChange: (value) => onChangeFilter("canonicalId", value),
      options: canonicalTopics.map((canonicalTopic) => ({
        value: canonicalTopic.id,
        label: canonicalTopic.label,
      })),
    },
  ]

  return (
    <FilterBar
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("conversations.filters.search")}
    />
  )
}

function mapIsLiveToLabel(isLive: boolean | undefined) {
  if (isNil(isLive)) return undefined
  return isLive ? LiveOrClosedEnum.Live : LiveOrClosedEnum.Closed
}

function mapBooleanFilterToLabel(value: boolean | undefined) {
  if (isNil(value)) return undefined
  return value ? "true" : "false"
}

function mapHasAttachmentsToLabel(hasAttachments: ("User" | "Assistant")[] | undefined) {
  if (!hasAttachments?.length) return undefined
  if (hasAttachments.length === 2) return "both"
  return hasAttachments[0] === "User" ? "user" : "assistant"
}

function mapLabelToHasAttachments(value: string | undefined) {
  if (value === "both") return ["User", "Assistant"]
  if (value === "user") return ["User"]
  if (value === "assistant") return ["Assistant"]
  return undefined
}

function mapHasSupportToLabel(hasSupport: ("questions" | "tickets" | "noTickets")[] | undefined) {
  if (!hasSupport?.length) return undefined
  if (hasSupport.includes("noTickets")) return "noTickets"
  if (hasSupport.length === 2) return "any"
  return hasSupport[0]
}

function mapLabelToHasSupport(value: string | undefined) {
  if (isNil(value)) return undefined
  if (value === "any") return ["questions", "tickets"]
  return [value]
}
